/**
 * CSV Export Utility
 * Provides functions for properly formatting and exporting data as CSV
 */

/**
 * Escape a value for CSV format
 * Handles commas, quotes, and newlines in field values
 */
export const escapeCsvValue = (value: any): string => {
	if (value === null || value === undefined) {
		return "";
	}

	const stringValue = String(value);

	// If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
	if (
		stringValue.includes(",") ||
		stringValue.includes('"') ||
		stringValue.includes("\n")
	) {
		return `"${stringValue.replace(/"/g, '""')}"`;
	}

	return stringValue;
};

/**
 * Convert an array of objects to CSV format
 * @param data - Array of objects to convert
 * @param headers - Optional array of header names (if not provided, uses object keys)
 * @param fieldMapper - Optional function to map object fields to CSV columns
 */
export const arrayToCsv = <T extends Record<string, any>>(
	data: T[],
	headers?: string[],
	fieldMapper?: (item: T) => string[],
): string => {
	if (!data || data.length === 0) {
		return headers ? headers.join(",") : "";
	}

	// If fieldMapper is provided, use it to extract values
	if (fieldMapper) {
		const rows = data.map((item) =>
			fieldMapper(item).map(escapeCsvValue).join(","),
		);
		return headers
			? `${headers.join(",")}\n${rows.join("\n")}`
			: rows.join("\n");
	}

	// Otherwise, use object keys
	const keys = headers || Object.keys(data[0]);
	const rows = data.map((item) =>
		keys.map((key) => escapeCsvValue(item[key] ?? "")).join(","),
	);

	return `${keys.map(escapeCsvValue).join(",")}\n${rows.join("\n")}`;
};

/**
 * Download CSV file
 * @param csvContent - CSV content as string
 * @param filename - Filename for the download (without extension)
 */
export const downloadCsv = (csvContent: string, filename: string): void => {
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};

/**
 * Format date for CSV export (ISO format for consistency)
 */
export const formatDateForCsv = (
	date: string | Date | null | undefined,
): string => {
	if (!date) return "";
	try {
		const dateObj = typeof date === "string" ? new Date(date) : date;
		if (isNaN(dateObj.getTime())) return "";
		return dateObj.toISOString().split("T")[0]; // YYYY-MM-DD format
	} catch {
		return "";
	}
};

/**
 * Format currency for CSV export (numeric value without currency symbol)
 */
export const formatCurrencyForCsv = (
	amount: number | string | null | undefined,
): string => {
	if (amount === null || amount === undefined || amount === "") {
		return "0";
	}
	const numValue = typeof amount === "string" ? parseFloat(amount) : amount;
	if (isNaN(numValue)) {
		return "0";
	}
	return numValue.toFixed(2);
};
